import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BoundingBoxOverlayVm, BoxVm } from './bounding-box-overlay.model';
import { ClassificationResult } from '@jump-tech-frontend/domain';
import { boxColour, boxDisplayLabel, confidencePercent, formatBoxLabel, primaryBoxes } from './bounding-boxes';

@Injectable()
export class BoundingBoxOverlayPresenter {
  private vm: BoundingBoxOverlayVm;
  private subject$: BehaviorSubject<BoundingBoxOverlayVm>;

  load(
    vmSubject$: BehaviorSubject<BoundingBoxOverlayVm>,
    imageIndex: number,
    questionKey: string,
    classificationResult: ClassificationResult | undefined,
    showOverlay: boolean
  ): void {
    this.subject$ = vmSubject$;

    const maskDefId = `inverse-mask-${questionKey}-${imageIndex}`;
    const maskId = `url(#${maskDefId})`;

    // Early return if we don't have bounding boxes data
    if (!classificationResult?.boundingBoxes || classificationResult.boundingBoxes.length === 0) {
      this.vm = {
        showOverlay: false,
        viewBox: '0 0 100 100',
        overlayStyles: { top: 0, left: 0, width: 0, height: 0 },
        boxes: [],
        hasBoxes: false,
        maskId,
        maskDefId
      };
      this.subject$.next(this.vm);
      return;
    }

    // Calculate image dimensions
    const dimensions = this.calculateImageDimensions(imageIndex, questionKey, classificationResult);

    // Calculate view box
    const viewBox = this.calculateViewBox(dimensions, classificationResult);

    // Transform bounding boxes - always calculate when they exist
    const boxes = this.transformBoundingBoxes(classificationResult.boundingBoxes, dimensions, classificationResult);

    this.vm = {
      showOverlay: showOverlay,
      viewBox,
      overlayStyles: {
        top: dimensions.overlayTop,
        left: dimensions.overlayLeft,
        width: dimensions.overlayWidth,
        height: dimensions.overlayHeight
      },
      boxes,
      hasBoxes: boxes.length > 0,
      maskId,
      maskDefId
    };

    this.subject$.next(this.vm);
  }

  private calculateImageDimensions(
    imageIndex: number,
    questionKey: string,
    classificationResult: ClassificationResult
  ): ImageDimensions {
    const imageId = questionKey + '_' + imageIndex + '_temp';
    const imgElement = document.getElementById(imageId) as HTMLImageElement;

    // If image element not found, it may not have rendered yet due to *ngIf="!!latestValue"
    if (!imgElement) {
      this.waitForImageElement(imageId, classificationResult);
      return this.createDimensionsFromAPI(classificationResult);
    }

    // Check if image is loaded and ready
    const isImageReady = imgElement.complete && imgElement.naturalWidth > 0;

    if (isImageReady) {
      // Image is loaded, calculate rendered dimensions immediately
      return this.calculateRenderedDimensions(imgElement, classificationResult);
    } else {
      // Image not loaded yet, add listener and recalculate when loaded
      imgElement.addEventListener(
        'load',
        () => {
          const dimensions = this.calculateRenderedDimensions(imgElement, classificationResult);
          this.updateOverlay(dimensions, classificationResult);
        },
        { once: true }
      );

      // Return API dimensions as placeholder
      return this.createDimensionsFromAPI(classificationResult);
    }
  }

  /**
   * Wait for the image element to appear in the DOM
   * Uses MutationObserver to watch for DOM changes
   */
  private waitForImageElement(imageId: string, classificationResult: ClassificationResult): void {
    const observer = new MutationObserver((mutations, obs) => {
      const imgElement = document.getElementById(imageId) as HTMLImageElement;
      if (imgElement) {
        obs.disconnect(); // Stop observing

        // Check if already loaded or wait for load event
        if (imgElement.complete && imgElement.naturalWidth > 0) {
          const dimensions = this.calculateRenderedDimensions(imgElement, classificationResult);
          this.updateOverlay(dimensions, classificationResult);
        } else {
          imgElement.addEventListener(
            'load',
            () => {
              const dimensions = this.calculateRenderedDimensions(imgElement, classificationResult);
              this.updateOverlay(dimensions, classificationResult);
            },
            { once: true }
          );
        }
      }
    });

    // Start observing the document body for child additions
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Safety timeout: stop observing after 5 seconds
    setTimeout(() => observer.disconnect(), 5000);
  }

  /**
   * Calculate the rendered dimensions of the image element
   * Uses getBoundingClientRect for accurate rendered size, with fallbacks
   */
  private calculateRenderedDimensions(
    imgElement: HTMLImageElement,
    classificationResult: ClassificationResult
  ): ImageDimensions {
    const container = imgElement.closest('.uploaded-image__img') as HTMLElement | null;

    // Try getBoundingClientRect first (most accurate for rendered size)
    const imgRect = imgElement.getBoundingClientRect();
    const contRect = container?.getBoundingClientRect();

    if (imgRect && imgRect.width > 0 && imgRect.height > 0) {
      const overlayLeft = contRect ? imgRect.left - contRect.left : 0;
      const overlayTop = contRect ? imgRect.top - contRect.top : 0;

      return {
        imageWidth: imgRect.width,
        imageHeight: imgRect.height,
        overlayTop,
        overlayLeft,
        overlayWidth: imgRect.width,
        overlayHeight: imgRect.height
      };
    }

    // Fallback to clientWidth/Height
    if (imgElement.clientWidth > 0 && imgElement.clientHeight > 0) {
      return {
        imageWidth: imgElement.clientWidth,
        imageHeight: imgElement.clientHeight,
        overlayTop: 0,
        overlayLeft: 0,
        overlayWidth: imgElement.clientWidth,
        overlayHeight: imgElement.clientHeight
      };
    }

    // Fallback to natural dimensions
    if (imgElement.naturalWidth > 0 && imgElement.naturalHeight > 0) {
      return {
        imageWidth: imgElement.naturalWidth,
        imageHeight: imgElement.naturalHeight,
        overlayTop: 0,
        overlayLeft: 0,
        overlayWidth: imgElement.naturalWidth,
        overlayHeight: imgElement.naturalHeight
      };
    }

    // Final fallback to API dimensions
    return this.createDimensionsFromAPI(classificationResult);
  }

  /**
   * Create ImageDimensions from API metadata
   */
  private createDimensionsFromAPI(classificationResult: ClassificationResult): ImageDimensions {
    const width = classificationResult.imageWidth || 0;
    const height = classificationResult.imageHeight || 0;

    return {
      imageWidth: width,
      imageHeight: height,
      overlayTop: 0,
      overlayLeft: 0,
      overlayWidth: width,
      overlayHeight: height
    };
  }

  /**
   * Update the overlay with new dimensions
   */
  private updateOverlay(dimensions: ImageDimensions, classificationResult: ClassificationResult): void {
    const viewBox = this.calculateViewBox(dimensions, classificationResult);
    const boxes = this.transformBoundingBoxes(classificationResult.boundingBoxes, dimensions, classificationResult);

    this.vm = {
      showOverlay: this.vm?.showOverlay ?? true, // Preserve current showOverlay state
      viewBox,
      overlayStyles: {
        top: dimensions.overlayTop,
        left: dimensions.overlayLeft,
        width: dimensions.overlayWidth,
        height: dimensions.overlayHeight
      },
      boxes,
      hasBoxes: boxes.length > 0,
      maskId: this.vm?.maskId ?? '',
      maskDefId: this.vm?.maskDefId ?? ''
    };

    this.subject$.next(this.vm);
  }

  private calculateViewBox(dimensions: ImageDimensions, result: ClassificationResult): string {
    const w = dimensions.imageWidth || result.imageWidth || 100;
    const h = dimensions.imageHeight || result.imageHeight || 100;
    return `0 0 ${w} ${h}`;
  }

  private transformBoundingBoxes(
    boxes: Array<{ label: string; x1: number; y1: number; x2: number; y2: number; confidence: number }>,
    dimensions: ImageDimensions,
    result: ClassificationResult
  ): BoxVm[] {
    const scaleX = this.calculateScaleX(dimensions, result);
    const scaleY = this.calculateScaleY(dimensions, result);

    return primaryBoxes(boxes, result.equipmentType).map(box => {
      const x = ((box.x1 ?? 0) * scaleX) | 0;
      const y = ((box.y1 ?? 0) * scaleY) | 0;
      const displayLabel = boxDisplayLabel(box);
      const labelBgWidth = this.calculateLabelWidth(displayLabel);

      return {
        x: String(x),
        y: String(y),
        width: String(Math.max(0, ((box.x2 ?? 0) - (box.x1 ?? 0)) * scaleX) | 0),
        height: String(Math.max(0, ((box.y2 ?? 0) - (box.y1 ?? 0)) * scaleY) | 0),
        color: boxColour(box.label),
        label: formatBoxLabel(box.label),
        confidence: confidencePercent(box.confidence),
        labelBgX: String(x + 2),
        labelBgY: String(y + 5),
        labelBgWidth: String(labelBgWidth),
        labelTextX: String(x + 8),
        labelTextY: String(y + 20),
        displayLabel
      };
    });
  }

  private calculateScaleX(dimensions: ImageDimensions, result: ClassificationResult): number {
    const destW = dimensions.imageWidth || result.imageWidth || 0;
    const srcW = result.sourceWidth || result.imageWidth || destW || 1;
    return srcW ? destW / srcW : 1;
  }

  private calculateScaleY(dimensions: ImageDimensions, result: ClassificationResult): number {
    const destH = dimensions.imageHeight || result.imageHeight || 0;
    const srcH = result.sourceHeight || result.imageHeight || destH || 1;
    return srcH ? destH / srcH : 1;
  }

  /**
   * Calculate approximate width for label background
   * Uses character-based estimation for 12px bold font
   * @param text - The label text with confidence
   * @returns Width in pixels
   */
  private calculateLabelWidth(text: string): number {
    const CHAR_WIDTH = 6.5; // Average pixels per character for 12px bold font
    const PADDING = 10; // Left + right padding (5px each side)
    return Math.ceil(text.length * CHAR_WIDTH) + PADDING;
  }
}

interface ImageDimensions {
  imageWidth: number;
  imageHeight: number;
  overlayTop: number;
  overlayLeft: number;
  overlayWidth: number;
  overlayHeight: number;
}
